// Utility function to decode JWT token
export const decodeJwtToken = (token) => {
  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token format');
    }
    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

// Function to extract claims (role, email, and modules) from token
export const getRoleFromToken = (token) => {
  const decoded = decodeJwtToken(token);
  if (decoded) {
    return {
      role: decoded.role || null,
      email: decoded.email || decoded.sub || null, // some JWT implementations use 'sub' for email
      modules: decoded.modules || [] // accessible modules for this user
    };
  }
  return null;
};

// Function to check if user has access to a specific module
export const hasModuleAccess = (token, moduleName) => {
  const claims = getRoleFromToken(token);
  if (!claims) return false;

  // SUPER_ADMIN and ADMIN have access to everything
  const role = claims.role?.toUpperCase();
  if (role === 'SUPER_ADMIN' || role === 'ADMIN') {
    return true;
  }

  // Check modules array
  return claims.modules && claims.modules.includes(moduleName);
};

// Function to determine the redirect path based on user's modules/permissions
export const getRedirectPath = (token) => {
  const claims = getRoleFromToken(token);
  console.log('getRedirectPath - claims:', claims);

  if (!claims) {
    console.log('getRedirectPath - no claims, returning /signin');
    return '/signin';
  }

  const role = claims.role?.toUpperCase();
  const modules = claims.modules || [];

  console.log('getRedirectPath - role:', role);
  console.log('getRedirectPath - modules:', modules);
  console.log('getRedirectPath - modules is array:', Array.isArray(modules));

  // SUPER_ADMIN and ADMIN always go to admin
  if (role === 'SUPER_ADMIN' || role === 'ADMIN') {
    console.log('getRedirectPath - SUPER_ADMIN/ADMIN role, returning /admin');
    return '/admin';
  }

  // GATEMEN always go to driver
  if (role === 'GATEMEN') {
    console.log('getRedirectPath - GATEMEN role, returning /driver');
    return '/driver';
  }

  // Check for admin-level modules FIRST (before checking role)
  // This allows users with role=USER but admin modules to access admin pages
  const adminModules = [
    'USER_MANAGEMENT', 'ROLE_MANAGEMENT', 'LOCATION_MASTER',
    'CONFERENCE_ROOM', 'VEHICLE_ENROLLMENT', 'VEHICLE_ASSIGNMENT',
    'ROUTE_MANAGEMENT', 'VEHICLE_ANALYTICS', 'DASHBOARD', 'REPORTS',
    'ANALYTICS', 'SYSTEM_SETTINGS', 'AUDIT_LOG', 'JOB_REQUISITION',
    'CANDIDATE_MANAGEMENT', 'JOB_MASTER_DATA', 'JOB_DASHBOARD', 'JOB_REPORTS'
  ];

  const hasAdminModule = modules.some(m => adminModules.includes(m));
  console.log('getRedirectPath - hasAdminModule:', hasAdminModule);

  if (hasAdminModule) {
    console.log('getRedirectPath - has admin modules, returning /admin');
    return '/admin';
  }

  // Check for user-level modules (Room Booking, Amenities)
  const userModules = ['ROOM_BOOKING', 'AMENITIES'];
  const hasUserModule = modules.some(m => userModules.includes(m));
  console.log('getRedirectPath - hasUserModule:', hasUserModule);

  if (hasUserModule) {
    console.log('getRedirectPath - has user modules, returning /user');
    return '/user';
  }

  // USER role without admin modules goes to user page
  if (role === 'USER') {
    console.log('getRedirectPath - USER role without special modules, returning /user');
    return '/user';
  }

  // Default: check if they have SYSTEM_LOGIN at minimum
  if (modules.includes('SYSTEM_LOGIN')) {
    console.log('getRedirectPath - has SYSTEM_LOGIN, returning /user');
    return '/user'; // Default to user page if they can login but have limited modules
  }

  // No recognized modules - deny access
  console.log('getRedirectPath - no recognized permissions, returning null');
  return null;
};

// Check whether a JWT token is expired (or invalid)
export const isTokenExpired = (token, skewSeconds = 10) => {
  try {
    if (!token || typeof token !== 'string') {
      return true;
    }
    const decoded = decodeJwtToken(token);
    // If no exp claim, treat as expired for safety
    if (!decoded || typeof decoded.exp !== 'number') {
      return true;
    }
    const currentEpochSeconds = Math.floor(Date.now() / 1000);
    // Add small negative skew to proactively treat near-expiry as expired
    return decoded.exp <= (currentEpochSeconds + skewSeconds);
  } catch (err) {
    return true;
  }
};

// Get token expiration time in milliseconds
export const getTokenExpirationTime = (token) => {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }
    const decoded = decodeJwtToken(token);
    if (!decoded || typeof decoded.exp !== 'number') {
      return null;
    }
    // Convert seconds to milliseconds
    return decoded.exp * 1000;
  } catch (err) {
    return null;
  }
};